import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import type { RootState } from '@center-pwa/store/store';
import { useApiClientsOrNull, useSphereSurfaces, FALLBACK_METASPACE_SYSTEM_NAME } from 'design-system/my-lib';

const SphereRedirector: React.FC = () => {
  const { sphereId } = useParams<{ sphereId: string }>();
  const userId = useSelector((state: RootState) => state.userProfile?.userId);
  const navigate = useNavigate();

  // Honor the sphere in the URL (e.g. /sphere/:sphereId from an identity switch
  // or a deep link) and only add the default metaspace. Fall back to the user's
  // home sphere ONLY for the bare /sphere route (no :sphereId).
  //
  // Previously this always redirected to `userId`, ignoring the URL sphere — so
  // navigating to any other sphere without a metaspace (which is exactly what an
  // identity switch does) bounced straight back to the user's own sphere, and the
  // navbar appeared not to reload.
  const target = sphereId && sphereId.trim() !== '' ? sphereId : userId;

  const apiClients = useApiClientsOrNull();
  const { defaultSpace, loading } = useSphereSurfaces(apiClients, target);

  useEffect(() => {
    if (!target || typeof target !== 'string' || target.trim() === '') {
      console.error('SphereRedirector: no sphere id available to redirect to.');
      return;
    }
    if (!apiClients || loading) return;

    const targetSystemName = defaultSpace?.systemName ?? FALLBACK_METASPACE_SYSTEM_NAME;
    navigate(`/sphere/${target}/metaspace/${targetSystemName}`, { replace: true });
  }, [target, apiClients, loading, defaultSpace, navigate]);

  return null;
};

export default SphereRedirector;
